import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faSearch, faTimes, faUser, faSignOutAlt, faFilm, faTv, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import { SearchAPI } from '../../api';
import NotificationBell from '../Notifications/NotificationBell';

const HeaderContainer = styled.header`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 98%;
  max-width: 1800px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background-color: transparent; /* removed black background */
  backdrop-filter: none; /* removed blur effect */
  border-radius: 8px;
  box-shadow: none; /* removed shadow */
  z-index: 100;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 0 15px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const MenuToggle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.15);
  cursor: pointer;
  transition: all var(--transition-speed);
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.3);
    transform: scale(1.05);
  }
`;

const SearchButton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.15);
  cursor: pointer;
  transition: all var(--transition-speed);
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.3);
    transform: scale(1.05);
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(26, 26, 26, 0.7);
  border-radius: 20px;
  padding: 8px 15px;
  width: 300px;
  border: 1px solid rgba(59, 130, 246, 0.4);
  transition: all var(--transition-speed);
  backdrop-filter: blur(5px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  
  &:focus-within {
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  @media (max-width: 768px) {
    display: ${props => props.$isSearchOpen ? 'flex' : 'none'};
    position: absolute;
    top: 70px;
    left: 15px;
    right: 15px;
    width: auto;
    background-color: rgba(26, 26, 26, 0.8);
    z-index: 100;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: var(--text-light);
  outline: none;
  width: 100%;
  margin-left: 10px;
  font-weight: 500;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Logo = styled(Link)`
  height: 45px;
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    height: 100%;
    object-fit: contain;
  }
`;

const ProfileButton = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    border-color: var(--primary-light);
    transform: scale(1.08);
    box-shadow: 0 5px 15px rgba(59, 130, 246, 0.3);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 100%;
    height: 100%;
    padding: 8px;
    background-color: rgba(59, 130, 246, 0.2);
    color: var(--text-light);
    transition: all 0.3s ease;
  }
  
  &:hover svg {
    background-color: rgba(59, 130, 246, 0.3);
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    border-width: 2.5px;
  }
`;

const LoginButton = styled.button`
  background-color: rgba(59, 130, 246, 0.8);
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 5px 15px rgba(59, 130, 246, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 7px 16px;
    font-size: 13px;
  }
`;

const UserMenu = styled.div`
  position: absolute;
  top: 60px;
  right: 20px;
  background-color: rgba(26, 26, 26, 0.75);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  z-index: 100;
  min-width: 200px;
  transform-origin: top right;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: 768px) {
    right: 15px;
    min-width: 180px;
  }
  
  @media (max-width: 480px) {
    right: 15px;
    min-width: 170px;
    top: 55px;
  }
`;

const UserMenuItem = styled.div`
  padding: 10px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 13px;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.2);
    transform: translateX(3px);
  }
  
  svg {
    margin-right: 10px;
    width: 14px;
    color: rgba(255, 255, 255, 0.8);
  }
  
  &.logout {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--error-color);
    
    &:hover {
      background-color: rgba(220, 38, 38, 0.15);
    }
    
    svg {
      color: rgba(220, 38, 38, 0.8);
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    
    svg {
      margin-right: 12px;
      width: 16px;
    }
  }
`;

const SearchSuggestions = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  right: 0;
  background-color: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  z-index: 10;
  max-height: 400px;
  overflow-y: auto;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuggestionItem = styled.div`
  padding: 12px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.15);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const SuggestionIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: ${props => props.$customColor || 'var(--primary-light)'};
  
  @media (max-width: 768px) {
    margin-right: 8px;
  }
`;

const SuggestionText = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SuggestionSubtitle = styled.div`
  font-size: 12px;
  color: var(--text-dim);
  margin-top: 2px;
`;

const SuggestionType = styled.div`
  font-size: 12px;
  color: var(--text-dim);
  margin-left: 8px;
  padding: 2px 6px;
  background-color: ${props => props.$customBg || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  border: 1px solid ${props => props.$customBorder || 'transparent'};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ContentIdentifier = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => props.$bgColor || 'rgba(255, 0, 0, 0.6)'};
  border: 1px solid ${props => props.$borderColor || 'rgba(255, 0, 0, 0.3)'};
  color: ${props => props.$textColor || 'white'};
  
  svg {
    margin-right: 2px;
    font-size: 7px;
  }
`;

// Helper function to extract content identifiers from title
const extractIdentifier = (title) => {
  if (!title) return { cleanTitle: '', identifier: null };
  
  // Define regex patterns for the identifiers
  const patterns = [
    { regex: /\(UNCENSORED\)/i, id: 'UNCENSORED', 
      style: { 
        bgColor: 'rgba(255, 0, 0, 0.6)', 
        borderColor: 'rgba(255, 0, 0, 0.3)',
        iconColor: 'rgba(255, 70, 70, 1)',
        typeBg: 'rgba(0, 0, 0, 0.7)', 
        typeBorder: 'rgba(255, 70, 70, 0.4)'
      }
    },
    { regex: /\(HUNGAMA\)/i, id: 'HUNGAMA', 
      style: { 
        bgColor: 'rgba(255, 165, 0, 0.6)', 
        borderColor: 'rgba(255, 165, 0, 0.3)',
        textColor: 'white',
        iconColor: 'rgba(255, 165, 0, 1)',
        typeBg: 'rgba(0, 0, 0, 0.7)', 
        typeBorder: 'rgba(255, 165, 0, 0.4)'
      }
    },
    { regex: /\(CN\)/i, id: 'CN', 
      style: { 
        bgColor: 'rgba(0, 150, 255, 0.6)', 
        borderColor: 'rgba(0, 150, 255, 0.3)',
        iconColor: 'rgba(0, 150, 255, 1)',
        typeBg: 'rgba(0, 0, 0, 0.7)', 
        typeBorder: 'rgba(0, 150, 255, 0.4)'
      }
    }
  ];
  
  let cleanTitle = title;
  let foundIdentifier = null;
  
  // Check for each pattern
  for (const pattern of patterns) {
    if (pattern.regex.test(title)) {
      // Remove the identifier from the title
      cleanTitle = title.replace(pattern.regex, '').trim();
      foundIdentifier = {
        id: pattern.id,
        style: pattern.style
      };
      break; // Stop after finding the first match
    }
  }
  
  return { cleanTitle, identifier: foundIdentifier };
};

const Header = () => {
  const { sidebarOpen, toggleSidebar } = useContext(AppContext);
  const { currentUser, openLoginModal, logout } = useContext(AuthContext);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const menuToggleRef = useRef(null);
  const suggestionsTimeout = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Show suggestions when typing
    if (value.trim().length >= 2) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  const fetchSuggestions = (query) => {
    // Clear previous timeout to avoid multiple requests
    if (suggestionsTimeout.current) {
      clearTimeout(suggestionsTimeout.current);
    }
    
    // Debounce the suggestions request
    suggestionsTimeout.current = setTimeout(async () => {
      if (query.trim().length < 2) return;
      
      setIsFetchingSuggestions(true);
      try {
        const response = await SearchAPI.getSuggestions(query);
        if (response.data && response.data.data) {
          setSuggestions(response.data.data);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setIsFetchingSuggestions(false);
      }
    }, 300); // 300ms debounce
  };

  const handleSearchSubmit = (e) => {
    // Prevent searching if the target is the episode search input
    if (e.target.id === 'episode-search-input' || 
        e.target.classList.contains('episode-search-input')) {
      return;
    }
    
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
      setIsSearchOpen(false);
      setShowSuggestions(false);
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'movie') {
      navigate(`/movie/${suggestion.id}`);
    } else if (suggestion.type === 'show') {
      navigate(`/show/${suggestion.id}`);
    } else if (suggestion.type === 'user') {
      navigate(`/profile/${suggestion.subtitle}`); // Navigate to user profile using username
    }
    
    setSearchValue('');
    setIsSearchOpen(false);
    setShowSuggestions(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };
  
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };
  
  const handleProfileClick = () => {
    navigate('/profile');
    setIsUserMenuOpen(false);
  };
  
  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  // Update click outside handler to close suggestions too
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close header search if clicking on episode search input
      if (event.target.closest('#episode-search-input') || 
          event.target.classList.contains('episode-search-input')) {
        return;
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuToggle 
          onClick={toggleSidebar} 
          ref={menuToggleRef}
          className="menu-toggle-button"
        >
          <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />
        </MenuToggle>
        <Logo to="/">
          <img src="/logo.png" alt="Logo" />
        </Logo>
      </LeftSection>
      
      <RightSection>
        <SearchButton onClick={toggleSearch}>
          <FontAwesomeIcon icon={faSearch} />
        </SearchButton>
        
        {isSearchOpen && (
          <SearchContainer ref={searchRef} $isSearchOpen={isSearchOpen}>
            <FontAwesomeIcon icon={faSearch} color="var(--primary-light)" />
            <SearchInput 
              type="text" 
              placeholder="Search Everything" 
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleSearchSubmit}
              autoFocus
            />
            
            {showSuggestions && suggestions.length > 0 && (
              <SearchSuggestions>
                {suggestions.map((suggestion, index) => {
                  // Extract identifier from suggestion title
                  const { cleanTitle, identifier } = extractIdentifier(suggestion.title);
                  
                  // Get custom styles based on identifier
                  let iconColor = null;
                  let typeStyles = {};
                  if (identifier) {
                    iconColor = identifier.style.iconColor;
                    typeStyles = {
                      $customBg: identifier.style.typeBg,
                      $customBorder: identifier.style.typeBorder
                    };
                  }
                  
                  return (
                    <SuggestionItem 
                      key={`${suggestion.type}-${suggestion.id}-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <SuggestionIcon $customColor={iconColor}>
                        {suggestion.type === 'movie' ? (
                          <FontAwesomeIcon icon={faFilm} />
                        ) : suggestion.type === 'show' ? (
                          <FontAwesomeIcon icon={faTv} />
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </SuggestionIcon>
                      <SuggestionText>
                        {cleanTitle}
                        {identifier && (
                          <ContentIdentifier 
                            $bgColor={identifier.style.bgColor}
                            $borderColor={identifier.style.borderColor}
                            $textColor={identifier.style.textColor}
                          >
                            {identifier.id === 'UNCENSORED' && <FontAwesomeIcon icon={faExclamationTriangle} />}
                            {identifier.id}
                          </ContentIdentifier>
                        )}
                        {suggestion.type === 'user' && suggestion.subtitle && (
                          <SuggestionSubtitle>@{suggestion.subtitle}</SuggestionSubtitle>
                        )}
                      </SuggestionText>
                      <SuggestionType {...typeStyles}>
                        {suggestion.type === 'movie' ? 'Movie' : suggestion.type === 'show' ? 'Show' : 'User'}
                      </SuggestionType>
                    </SuggestionItem>
                  );
                })}
              </SearchSuggestions>
            )}
          </SearchContainer>
        )}
        
        {currentUser && <NotificationBell />}
        
        {currentUser ? (
          <>
            <ProfileButton onClick={toggleUserMenu}>
              {currentUser.avatar ? (
                <img src={`/avatars/${currentUser.avatar}`} alt="User profile" />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
            </ProfileButton>
            
            {isUserMenuOpen && (
              <UserMenu>
                <UserMenuItem onClick={handleProfileClick}>
                  <FontAwesomeIcon icon={faUser} />
                  My Profile
                </UserMenuItem>
                <UserMenuItem className="logout" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  Logout
                </UserMenuItem>
              </UserMenu>
            )}
          </>
        ) : (
          <LoginButton onClick={openLoginModal}>
            Login
          </LoginButton>
        )}
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;